import { createApi } from '@reduxjs/toolkit/query/react';

import type { OTCListResponseSchemaType, OTCResponseSchemaType, OTCSchemaType } from 'src/global/schemas/otc.schema';
import { MetaRequest } from 'src/global/types/otc.type';

import { REDUX_SLICE } from '../constants';
import { axiosBaseQuery } from '../utils';

export const otcApi = createApi({
  reducerPath: REDUX_SLICE.OTC_API,
  tagTypes: ['OTCList', 'OTCDetail'],
  baseQuery: axiosBaseQuery,
  endpoints: builder => ({
    createOTC: builder.mutation<OTCResponseSchemaType, { programId?: string; payload: OTCSchemaType; creator: string }>(
      {
        query: ({ programId, payload, creator }) => ({
          method: 'POST',
          path: `/v1/programs/${programId}/deals`,
          headers: {
            'x-nodesale-lang': 'en',
            'x-offchain-address': creator,
          },
          data: { ...payload },
          shouldSign: true,
        }),
        transformResponse: (returnValue: OTCResponseSchemaType) => {
          return returnValue;
        },
        invalidatesTags: ['OTCList'],
      },
    ),
    getOTCSales: builder.query<OTCSchemaType[], { programId?: string; creator?: string; params?: MetaRequest }>({
      query: ({ programId, creator, params }) => ({
        path: `/v1/programs/${programId}/deals`,
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': creator,
        },
        params,
      }),
      transformResponse: (returnValue: OTCListResponseSchemaType) => {
        return returnValue.data;
      },
      providesTags: (_result, _error, arg) => [
        { type: 'OTCList', programId: arg.programId, creator: arg.creator, params: arg.params },
      ],
    }),
    getOTCDetail: builder.query<
      OTCSchemaType,
      { programId?: string; creator?: string; params?: MetaRequest; otcId: string }
    >({
      query: ({ programId, creator, params, otcId }) => ({
        path: `/v1/programs/${programId}/deals/${otcId}`,
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': creator,
        },
        params,
      }),
      transformResponse: (returnValue: OTCResponseSchemaType) => {
        return returnValue.data;
      },
      providesTags: ['OTCDetail'],
    }),
    deleteOTC: builder.mutation<OTCResponseSchemaType, { programId?: string; creator: string; otcId?: string }>({
      query: ({ programId, creator, otcId }) => ({
        method: 'DELETE',
        path: `/v1/programs/${programId}/deals/${otcId}`,
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': creator,
        },
        shouldSign: true,
      }),
      transformResponse: (returnValue: OTCResponseSchemaType) => {
        return returnValue;
      },
      invalidatesTags: ['OTCList'],
    }),
  }),
});

export const { useCreateOTCMutation, useGetOTCSalesQuery, useGetOTCDetailQuery, useDeleteOTCMutation } = otcApi;
